import { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { CheckboxInput, ErrorMessage, Typography } from "@/components/atoms";
import { useProfile } from "@/hooks";
import { VehicleDocumentDateInput } from "../common/VehicleDocumentDateInput";
import { VehicleDocumentUploadField } from "../common/VehicleDocumentUpload";
import { useEditVehicleContext } from "../context";
import { EditVehicleFormData } from "../form";
import { useVehicleDocuments } from "../hooks/useVehicleDocuments";

export const VehicleDocuments = () => {
  const { dateFormat } = useProfile();
  const { vehicle, setDocumentRetryCount, setIsDocumentValidating } = useEditVehicleContext();

  const {
    control,
    watch,
    register,
    setValue,
    clearErrors,
    formState: { errors, dirtyFields },
  } = useFormContext<EditVehicleFormData>();

  const {
    accreditationDoc,
    insuranceDoc,
    insuranceExpiry,
    insuranceExpiryConfirm,
    accreditationExpiry,
    accreditationExpiryConfirm,
  } = watch("documents");

  // Once the user has satisfied the manual-entry requirements (date + confirm),
  // suppress the "couldn't be verified" prompt — it's served its purpose.
  const suppressInsuranceMessage = !!insuranceExpiry && !!insuranceExpiryConfirm;
  const suppressAccreditationMessage = !!accreditationExpiry && !!accreditationExpiryConfirm;

  const documents = useVehicleDocuments({
    vehicleId: vehicle.uuid,
    insuranceId: insuranceDoc ?? undefined,
    accreditationId: accreditationDoc ?? undefined,
  });

  useEffect(() => {
    setValue("documents.insuranceAutoverified", !documents.insurance.manualExpiryDate);
    setValue("documents.accreditationAutoverified", !documents.accreditation.manualExpiryDate);
  }, [documents.accreditation.manualExpiryDate, documents.insurance.manualExpiryDate, setValue]);

  useEffect(() => {
    setIsDocumentValidating(documents.isProcessing);
  }, [documents.isLoading, documents.isProcessing, setIsDocumentValidating]);

  return (
    <>
      <label htmlFor="documents.insuranceDoc" className="col-span-2 md:col-span-1">
        <Typography className="leading-[2rem]">Vehicle Insurance Document</Typography>
        <Controller
          control={control}
          name="documents.insuranceDoc"
          render={({ field }) => (
            <VehicleDocumentUploadField
              document={documents.insurance.document}
              documentType="INSURANCE"
              onUpload={(uuid) => {
                field.onChange(uuid);
                setDocumentRetryCount((prev) => ({ ...prev, insurance: 0 }));
                setValue("documents.insuranceExpiry", null);
                setValue("documents.insuranceExpiryConfirm", false);
                clearErrors(["documents.insuranceExpiry", "documents.insuranceExpiryConfirm"]);
              }}
              onRemove={() => {
                field.onChange(null);
                setDocumentRetryCount((prev) => ({ ...prev, insurance: 0 }));
                setValue("documents.insuranceExpiry", null);
                setValue("documents.insuranceExpiryConfirm", false);
                clearErrors(["documents.insuranceExpiry", "documents.insuranceExpiryConfirm"]);
              }}
              hasError={!!errors.documents?.insuranceDoc}
            />
          )}
        />
        {errors.documents?.insuranceDoc?.message ? (
          <ErrorMessage className="mt-2 text-sm" name="documents" errors={errors} message={errors.documents?.insuranceDoc?.message} />
        ) : (
          <Typography className="mt-2 text-sm text-danger empty:hidden">
            {suppressInsuranceMessage ? "" : documents.insurance.errorMessage}
          </Typography>
        )}
      </label>
      {documents.insurance.document ? (
        <label htmlFor="documents.insuranceExpiry" className="col-span-2 md:col-span-1">
          <Typography className="leading-[2rem]">Insurance Expiry Date</Typography>
          <Controller
            name="documents.insuranceExpiry"
            control={control}
            render={({ field }) => {
              const selected =
                !documents.insurance.manualExpiryDate && documents.insurance.document?.expiryDate
                  ? new Date(documents.insurance.document.expiryDate)
                  : field.value;

              return (
                <VehicleDocumentDateInput
                  className="w-full"
                  onChange={(date) => {
                    field.onChange(date);
                    setValue("documents.insuranceExpiryConfirm", false);
                  }}
                  selected={selected}
                  placeholderText="-- / -- / ----"
                  hasError={!!errors.documents?.insuranceExpiry}
                  dateFormat={dateFormat}
                  disabled={documents.insurance.isLoading || !documents.insurance.manualExpiryDate || !dirtyFields.documents?.insuranceDoc}
                />
              );
            }}
          />
          {dirtyFields.documents?.insuranceDoc && documents.insurance.manualExpiryDate && !documents.insurance.isLoading && (
            <div className="mt-2">
              <CheckboxInput
                {...register("documents.insuranceExpiryConfirm")}
                id="confirm-insurance"
                label="I confirm that I am responsible for the date entered"
                className="text-neutral-dark-gray [&_.typography]:!text-sm"
                variant="check"
              />
            </div>
          )}
          {errors.documents?.insuranceExpiry?.message && documents.insurance.manualExpiryDate && (
            <ErrorMessage className="text-sm" name="documents" errors={errors} message={errors.documents.insuranceExpiry.message} />
          )}
        </label>
      ) : (
        <label></label>
      )}

      <label htmlFor="documents.accreditationDoc" className="col-span-2 md:col-span-1">
        <Typography className="leading-[2rem]">Vehicle Accreditation Document (Optional)</Typography>
        <Controller
          control={control}
          name="documents.accreditationDoc"
          render={({ field }) => (
            <VehicleDocumentUploadField
              document={documents.accreditation.document}
              documentType="ACCREDITATION"
              onUpload={(uuid) => {
                field.onChange(uuid);
                setDocumentRetryCount((prev) => ({ ...prev, accreditation: 0 }));
                setValue("documents.accreditationExpiry", null);
                setValue("documents.accreditationExpiryConfirm", false);
                clearErrors(["documents.accreditationExpiry", "documents.accreditationExpiryConfirm"]);
              }}
              onRemove={() => {
                field.onChange(null);
                setDocumentRetryCount((prev) => ({ ...prev, accreditation: 0 }));
                setValue("documents.accreditationExpiry", null);
                setValue("documents.accreditationExpiryConfirm", false);
                clearErrors(["documents.accreditationExpiry", "documents.accreditationExpiryConfirm"]);
              }}
              hasError={!!errors.documents?.accreditationDoc}
            />
          )}
        />
        {errors.documents?.accreditationDoc?.message ? (
          <ErrorMessage className="mt-2 text-sm" name="documents" errors={errors} message={errors.documents?.accreditationDoc?.message} />
        ) : (
          <Typography className="mt-2 text-sm text-danger empty:hidden">
            {suppressAccreditationMessage ? "" : documents.accreditation.errorMessage}
          </Typography>
        )}
      </label>
      {documents.accreditation.document ? (
        <label htmlFor="documents.accreditationExpiry">
          <Typography className="leading-[2rem]">Accreditation Expiry Date (Optional)</Typography>
          <Controller
            name="documents.accreditationExpiry"
            control={control}
            render={({ field }) => {
              const selected =
                !documents.accreditation.manualExpiryDate && documents.accreditation.document?.expiryDate
                  ? new Date(documents.accreditation.document.expiryDate)
                  : field.value;

              return (
                <VehicleDocumentDateInput
                  className="w-full"
                  onChange={(date) => {
                    field.onChange(date);
                    setValue("documents.accreditationExpiryConfirm", false);
                  }}
                  selected={selected}
                  placeholderText="-- / -- / ----"
                  hasError={!!errors.documents?.accreditationExpiry}
                  dateFormat={dateFormat}
                  disabled={
                    documents.accreditation.isLoading ||
                    !documents.accreditation.manualExpiryDate ||
                    !dirtyFields.documents?.accreditationDoc
                  }
                />
              );
            }}
          />
          {dirtyFields.documents?.accreditationDoc && documents.accreditation.manualExpiryDate && !documents.accreditation.isLoading && (
            <div className="mt-2">
              <CheckboxInput
                {...register("documents.accreditationExpiryConfirm")}
                id="confirm-accreditation"
                label="I confirm that I am responsible for the date entered"
                className="text-neutral-dark-gray [&_.typography]:!text-sm"
                variant="check"
              />
            </div>
          )}
          {errors.documents?.accreditationExpiry?.message && documents.accreditation.manualExpiryDate && (
            <ErrorMessage className="text-sm" name="documents" errors={errors} message={errors.documents?.accreditationExpiry.message} />
          )}
        </label>
      ) : (
        <label></label>
      )}
    </>
  );
};
